import React, { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { OpenTokContext } from '../../../contexts/opentok/opentok-context';
import { AVContext } from '../../../contexts/audio-video';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { GreenroomState as GRS } from '../../../stores/types/types';
import { ConnectedUser } from '../../../connection/socket';

interface DisplayRow {
	key: string;
	gr_user: JSX.Element;
	audio?: boolean;
	video?: boolean;
	email?: string;
}

export default function DebugVideo() {
	const [refresh, setRefresh]: [number, React.Dispatch<React.SetStateAction<number>>] = useState(1);

	const { opentok } = useContext(OpenTokContext);

	const { getAllAV } = useContext(AVContext);

	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const clientWebrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const clientUser = useSelector((s: GRS) => s.SessionReducer.user);

	const streams = useMemo(
		(): string[] => Array.from(opentok?.streams.keys() ?? []),
		[opentok?.streams, refresh],
	);

	const mypubs = useMemo(
		(): any [] => opentok?.myPublishers?.map((p) => p.stream?.streamId ?? 'no id') ?? [],
		[opentok?.streams, refresh],
	);

	const displayData: DisplayRow[] = useMemo(() => {
		Object.keys(connectedUsers);
		const avObject = Object.keys(getAllAV());

		const newDisplayData: DisplayRow[] = [];
		avObject.forEach((key) => {
			const matcher = (user: ConnectedUser) => user.webrtcID === key;
			const userObj = connectedUsers.find(matcher);
			if (key === 'local') {
				const newRow = {
					key,
					audio: getAllAV()[key].audio,
					video: getAllAV()[key].video,
					email: `${clientUser?.email ?? 'no email'}`,
					gr_user: <span style={{ color: 'rgb(43, 255, 248)' }}>local ({clientUser?.gr_user ?? 'missing id'})</span>,
				};
				newDisplayData.push(newRow);
			} else {
				const newRow = {
					key,
					audio: getAllAV()[key].audio,
					video: getAllAV()[key].video,
					email: userObj?.email ?? '',
					gr_user: userObj?.gr_user
						? <span style={{ color: 'rgb(43, 255, 248)' }}>{userObj?.gr_user}</span>
						: <><span style={{ color: 'rgb(255, 3, 130)' }}>None</span>, no ConnectedUser found</>,
				};
				newDisplayData.push(newRow);
			}
		});

		return newDisplayData;
	}, [connectedUsers, refresh, getAllAV, refresh]);

	return (
		<div className="section memory-dashboard">
			<h5>AV & ConnectedUser Dataset</h5>
			{displayData.length === 0 && <div>No AV Data</div>}
			{displayData.map((row, index) => (
				<div key={row.key} style={{ padding: '4px 0', lineHeight: 1.3 }}>
					<div>{index + 1}. <span>user {row.gr_user} {row.email}</span>
					<div style={{ paddingBottom: 3, display: 'flex', alignContent: 'center' }}>
						<Icon
							name={row.video ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
							color={row.video ? COLORS.WHITE : COLORS.RED}
							size={16}
						/>
						&nbsp;
						<Icon
							name={row.audio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
							color={row.audio ? COLORS.WHITE : COLORS.RED}
							size={16}
						/>
						Key:&nbsp;<span style={{ color: '#B9E2B6' }}>{row.key}</span></div>
					</div>
				</div>
			))}

			<h5>Webrtc ids </h5>
			<div className='padding-bottom-small'>
				SessionReducer.webrtcID (redux)
				<div style={{ color: '#B9E2B6' }}>
					{clientWebrtcID ?? 'null'}
				</div>
			</div>
			<div className='padding-bottom-small'>
				OTClass.publisher.streamId
				<div style={{ color: '#B9E2B6' }}>
					{opentok?.publisher?.streamId ?? 'null'}
				</div>
			</div>

			<div className='padding-bottom-small'>
				OTClass.myPublishers.stream.streamId
				<div style={{ color: '#B9E2B6' }}>
					{mypubs.map((id, i) => <div key={`${id}mypub`}>{i + 1}. {id}</div>)}
				</div>
			</div>

			<div className='padding-bottom-small'>
				OTClass.streams keys
				<div style={{ color: '#B9E2B6' }}>
					{streams.map((webrtcid, i) => <div key={`${webrtcid}webrtcid`}>{i + 1}. {webrtcid}</div>)}
				</div>
			</div>
			<button
				style={{ padding: '8px 12px', marginBottom: '16px', outline: '3px solid #A6FF79' }}
				onClick={() => setRefresh((p) => p + 1)}>
				Trigger dependency arrays for shown data [ {refresh} ]
			</button>
			<div style={{ fontSize: '12px' }}>
				Add <span style={{ color: '#B9E2B6' }}>"?debug=opentok"</span> query string to enable hotkeys
				{/* // see src/utils/video/opentok-connection.ts for more details */}
				<div>[ <span style={{ color: '#B9E2B6' }}>E</span> ] - Failed Connection</div>
				<div>[ <span style={{ color: '#B9E2B6' }}>R</span> ] - Reconnection</div>
			</div>

			&nbsp;
			<h5>Audio Bitrates</h5>
			<div className='padding-bottom-small'>
				{Object.keys(opentok?.stats ?? {}).map((key) => <div key={`${key}key`}><span style={{ color: '#B9E2B6' }}>{key}</span>: {opentok?.stats[key].audioBitrate}</div>)}
			</div>

			&nbsp;
		</div>
	);
}
